import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { cn } from "@gettactic/helpers/src/css/cn";
import { Lock } from "lucide-react";
import React from "react";
import type {
  ResourceStatus,
  ResourceStatusTone
} from "@/features/map/helpers/resourceStatus";

/** Tone → band classes (bg, text and top/bottom border). Token-based so both
 * themes stay legible. */
const toneClasses: Record<ResourceStatusTone, string> = {
  green: "bg-green-50 text-green-700 border-green-100",
  blue: "bg-sky-50 text-sky-700 border-sky-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  neutral: "bg-neutral-100 text-neutral-600 border-neutral-200",
  indigo: "bg-indigo-50 text-primary border-indigo-100"
};

interface ResourceStatusBandProps {
  status: ResourceStatus;
  /**
   * Show a "Restricted" tag alongside the status. Used for a desk that is
   * limited to some teams/users but has no assigned owner, so its real
   * availability stays the primary status.
   */
  restricted?: boolean;
  /** Extra classes (e.g. the `-mx-3` used to break out of the popover padding). */
  className?: string;
}

/**
 * Full-width status strip shown directly under the header of the on-click
 * resource card. Renders the derived {@link ResourceStatus} icon + label, plus
 * an optional trailing "Restricted" tag.
 */
export const ResourceStatusBand: React.FC<ResourceStatusBandProps> = ({
  status,
  restricted = false,
  className
}) => {
  const { Icon, label, tone, detail } = status;
  return (
    <div
      className={cn(
        "border-y px-3 py-2 font-semibold text-xs tracking-wide",
        toneClasses[tone],
        className
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 truncate">{label}</span>
        {restricted ? (
          <span className="ml-auto flex shrink-0 items-center gap-1 rounded-full bg-white/60 px-2 py-0.5 text-[10px] text-neutral-600">
            <Lock className="h-3 w-3" aria-hidden="true" />
            Restricted
          </span>
        ) : null}
      </div>
      {detail ? (
        // Its own line, under the label, so the owner's name never pushes
        // the label out of the band.
        <div className="mt-1.5 flex min-w-0 items-center gap-1.5 pl-6 font-normal text-[11px] tracking-normal">
          <span className={detail.user ? "shrink-0" : "min-w-0 truncate"}>
            {detail.text}
          </span>
          {detail.user ? (
            <>
              <UserAvatar user={detail.user} size="xs" />
              <span className="truncate font-medium">{detail.user.name}</span>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
